import { api } from './api';
import { getAllBadges } from './badges.service';

export interface BoardsInterface {
  extra: any;
  title: string;
  principalType: number;
  operation: BoardsOperation;
  period: BoardsPeriod;
  techniques: string[];
  _id: string;
}

interface BoardsPeriod {
  type: number;
  timeAmount: number;
  timeScale: number;
}

interface BoardsOperation {
  type: number;
  achievement_type: number;
  sort: number;
  sub: boolean;
  prepared: string;
}

export interface LeaderboardInterface {
  leaderboard: LeaderboardInfo;
  start_datetime: string;
  end_datetime: string;
  creation_datetime: string;
  expiration_datetime: string;
  leaders: LeaderboardLeaders[];
  my_position?: LeaderboardLeaders;
}

interface LeaderboardLeaders {
  boardId: string;
  player: string;
  name: string;
  position: number;
  total: number;
  previous_position: number;
  previous_total: number;
  move: string;
  extra: any;
  _id: string;
  image?: string;
}

interface LeaderboardInfo {
  title: string;
  description: string;
  principalType: number;
  operation: LeaderboardInfoOperation;
  period: LeaderboardInfoPeriod;
  techniques: string[];
  _id: string;
}

interface LeaderboardInfoPeriod {
  type: number;
  timeAmount: number;
  timeScale: number;
  startDate: number;
  endDate: number;
}

interface LeaderboardInfoOperation {
  type: number;
  achievement_type: number;
  sort: number;
  sub: boolean;
  prepared: string;
}

export async function getLeaderboards(): Promise<BoardsInterface[]> {
  try {
    const { data } = await api.get(`leaderboard`);

    if (!Array.isArray(data)) {
      return [];
    } else {
      return data;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function getLeaderboard(
  leaderboardId: string,
  userId: string,
  maxResults = -1,
  dates = '-10y-;-0y%2B',
  viewType = 1,
): Promise<any> {
  //leaderboard/Dyr1JQS/leader/aggregate?view=1&period=-10y%3B-0d%2B&live=true&player=inesmari.bekon%40condor.ind.br&limit=10
  const { data } = await api.post(
    `leaderboard/${leaderboardId}/leader/aggregate?view=${viewType}&period=${dates}&live=true&player=${userId}&limit=${maxResults}`,
    [],
  );

  return data;
}

export async function getPlayerPosition(leaderboardId: string, userId: string, cache?: string): Promise<LeaderboardLeaders> {
  if (!leaderboardId || !userId) throw new Error('"leaderboardId" and "userId" parameters are required.');

  try {
    const payload: any = {
      $match: {
        boardId: leaderboardId,
        player: userId,
      },
    };

    if (typeof cache === 'string') {
      payload.$match['extra.cache'] = cache;
    }

    const { data } = await api.post(`database/leader/aggregate`, [{ ...payload }]);

    if (!Array.isArray(data)) {
      let message = '';
      if (data.errorMessage) message = data.errorMessage;
      throw new Error(message);
    } else if (!data.length) {
      throw new Error('Player position not found.');
    } else {
      return data[0];
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function enrichLeadersWithBadges(leaders: LeaderboardLeaders[]): Promise<Record<string, { achievements: any[] }>> {
  try {
    // Extrair IDs dos players
    const playerIds = leaders.map(leader => leader.player).filter(id => !!id);

    if (playerIds.length === 0) {
      return {};
    }

    // Buscar badges GT02
    const allBadges = await getAllBadges();

    // Buscar achievements dos players
    const { data: playersWithAchievements } = await api.post('/database/player/aggregate', [
      {
        $match: {
          _id: {
            $in: playerIds,
          },
        },
      },
      {
        $lookup: {
          from: 'achievement',
          let: {
            playerId: '$_id',
          },
          pipeline: [
            {
              $match: {
                $expr: {
                  $and: [
                    {
                      $eq: ['$player', '$$playerId'],
                    },
                    {
                      $eq: ['$type', 1],
                    },
                  ],
                },
              },
            },
          ],
          as: 'achievements',
        },
      },
    ]);

    // Enriquecer com badges GT02
    const enrichedData: Record<string, any> = {};

    playersWithAchievements.forEach((player: any) => {
      const gt02Badges = (player.achievements || []).filter((achievement: any) => {
        const badge = allBadges.find(b => b._id === achievement.item);
        return badge && badge.techniques?.includes('GT02');
      });

      enrichedData[player._id] = {
        achievements: gt02Badges,
      };
    });

    return enrichedData;
  } catch (error) {
    console.error('Erro ao enriquecer leaders com badges:', error);
    return {};
  }
}
